import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import CourseWizard from './CourseWizard';
import StudentAnalytics from './StudentAnalytics';
import FacultyProfile from './FacultyProfile';
import FacultyCommunication from '../communication/FacultyCommunication';
import FacultyCertificates from './FacultyCertificates';
import './FacultyDashboard.css';
import Toast from './Toast.jsx';
import Loader from './Loader.jsx';

const FacultyDashboard = () => {
  // Navigation & UI State
  const [activeSection, setActiveSection] = useState('courses');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [wizardMode, setWizardMode] = useState('create'); // 'create' or 'edit'
  const [editingCourseId, setEditingCourseId] = useState(null);
  const [showStudentDetail, setShowStudentDetail] = useState(false);

  // Data State
  const [courses, setCourses] = useState([]);
  const [students, setStudents] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [facultyProfile, setFacultyProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  // Filters & Selection
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [courseFilter, setCourseFilter] = useState('');

  // User Context
  const [facultyId, setFacultyId] = useState(localStorage.getItem('fac_id'));
  const [facultyName, setFacultyName] = useState(localStorage.getItem('faculty_name') || 'Faculty');

  // Statistics State
  const [stats, setStats] = useState({
    total_courses: 0,
    published_courses: 0,
    total_students: 0,
    pending_evaluations: 0
  });

  // Load All Dashboard Data
  const loadAllData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Resolve faculty ID if not already in localStorage
      let resolvedFacultyId = facultyId;
      if (!resolvedFacultyId) {
        const user = localStorage.getItem('user');
        if (user) {
          try {
            const userData = JSON.parse(user);
            console.log('[FacultyDashboard] User object from storage:', userData);
            
            // Try to fetch faculty ID from user_id
            if (userData.user_id) {
              const facultyRes = await axios.get(`/api/faculty/by-user/${userData.user_id}`);
              resolvedFacultyId = facultyRes.data.fac_id;
              setFacultyId(resolvedFacultyId);
              console.log('[FacultyDashboard] Got fac_id from user_id:', resolvedFacultyId);
            }
          } catch (e) {
            console.error('[FacultyDashboard] Error extracting faculty ID:', e);
          }
        }
      }
      
      if (!resolvedFacultyId) {
        throw new Error('Faculty ID not found. Please login again.');
      }
      
      console.log('[FacultyDashboard] Loading data for faculty:', resolvedFacultyId);

      // Load faculty profile
      const profileRes = await axios.get(`/api/faculty/${resolvedFacultyId}`);
      console.log('[FacultyDashboard] Profile response:', profileRes.data);
      setFacultyProfile(profileRes.data);

      // Load courses with proper fac_id parameter
      const coursesRes = await axios.get(`/api/faculty/courses?fac_id=${resolvedFacultyId}`);
      console.log('[FacultyDashboard] Courses response:', coursesRes.data);
      const coursesData = Array.isArray(coursesRes.data) ? coursesRes.data : [];
      setCourses(coursesData);

      // Load students with proper fac_id parameter
      const studentsRes = await axios.get(`/api/faculty/${resolvedFacultyId}/students`);
      console.log('[FacultyDashboard] Students response:', studentsRes.data);
      const studentsData = Array.isArray(studentsRes.data) ? studentsRes.data : [];
      setStudents(studentsData);

      // Load programs for wizard
      try {
        const programsRes = await axios.get('/api/programs');
        setPrograms(Array.isArray(programsRes.data) ? programsRes.data : []);
      } catch (error) {
        console.warn('[FacultyDashboard] Failed to load programs:', error);
        setPrograms([]);
      }

      // Calculate statistics
      const publishedCount = coursesData.filter(c => (c.publish_status || '').toLowerCase() === 'published').length;
      const uniqueStudents = new Set(studentsData.map(s => s.student_id)).size;

      setStats({
        total_courses: coursesData.length,
        published_courses: publishedCount,
        total_students: uniqueStudents,
        pending_evaluations: 0
      });
      
      console.log('[FacultyDashboard] Stats calculated:', { courses: coursesData.length, published: publishedCount, students: uniqueStudents });

      setToast({ type: 'success', message: 'Dashboard loaded successfully' });
    } catch (error) {
      console.error('[FacultyDashboard] Failed to load dashboard data:', error);
      setToast({ 
        type: 'error', 
        message: `Failed to load data: ${error.response?.data?.error || error.message}` 
      });
    } finally {
      setLoading(false);
    }
  }, [facultyId]);

  // Load data on mount
  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // Fetch course-specific students when courseFilter changes
  useEffect(() => {
    const loadCourseStudents = async () => {
      if (courseFilter) {
        try {
          const res = await axios.get(`/api/courses/${courseFilter}/students`);
          console.log('[FacultyDashboard] Course students:', res.data);
          const studentsData = Array.isArray(res.data) ? res.data : (res.data?.students || []);
          setStudents(studentsData);
        } catch (error) {
          console.error('[FacultyDashboard] Failed to load course students:', error);
          setStudents([]);
          setToast({ 
            type: 'error', 
            message: `Failed to load course students: ${error.response?.data?.error || error.message}` 
          });
        }
      }
    };
    
    if (courseFilter) {
      loadCourseStudents();
    }
  }, [courseFilter]);

  // Auto-filter students when a specific course is selected from course view
  useEffect(() => {
    if (selectedCourse && activeSection === 'students') {
      setCourseFilter(selectedCourse);
    }
  }, [selectedCourse, activeSection]);

  const handlePublish = async (courseId) => {
    try {
      const response = await axios.put(`/api/courses/${courseId}/publish?fac_id=${facultyId}`);
      setToast({ type: 'success', message: 'Course published successfully!' });
      loadAllData();
    } catch (error) {
      setToast({ type: 'error', message: error.response?.data?.error || 'Failed to publish course' });
    }
  };

  const handleUnpublish = async (courseId) => {
    try {
      const response = await axios.put(`/api/courses/${courseId}/unpublish?fac_id=${facultyId}`);
      setToast({ type: 'success', message: 'Course unpublished successfully!' });
      loadAllData();
    } catch (error) {
      setToast({ type: 'error', message: error.response?.data?.error || 'Failed to unpublish course' });
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm('Are you sure you want to delete this course? This action cannot be undone.')) {
      return;
    }
    try {
      await axios.delete(`/api/courses/${courseId}`);
      setToast({ type: 'success', message: 'Course deleted successfully!' });
      loadAllData();
    } catch (error) {
      setToast({ type: 'error', message: 'Failed to delete course' });
    }
  };

  const handleEditCourse = (courseId) => {
    setEditingCourseId(courseId);
    setWizardMode('edit');
    setShowWizard(true);
  };

  const handleCreateCourse = () => {
    setEditingCourseId(null);
    setWizardMode('create');
    setShowWizard(true);
  };

  // Filter students based on search and course
  const filteredStudents = (Array.isArray(students) ? students : []).filter(student => {
    const matchesSearch = student.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          student.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCourse = !courseFilter || student.course_id === courseFilter;
    return matchesSearch && matchesCourse;
  });

  // Render Loaders
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Loader />
      </div>
    );
  }

  return (
    <div className="faculty-layout">
      {/* Sidebar */}
      <aside className={`faculty-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        {/* Logo */}
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <span className="logo-icon">🏫</span>
            {sidebarOpen && <span className="logo-text">RCMAS RE-LEARNING</span>}
          </div>
          <button 
            className="sidebar-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            title={sidebarOpen ? 'Close' : 'Open'}
          >
            {sidebarOpen ? '✕' : '☰'}
          </button>
        </div>

        {/* User Card */}
        {sidebarOpen && (
          <div className="sidebar-user-card">
            <div className="user-avatar-large">{facultyName.charAt(0).toUpperCase()}</div>
            <h3>{facultyName}</h3>
            <p>Faculty Dashboard</p>
          </div>
        )}

        {/* Navigation */}
        <nav className="sidebar-nav">
          <button
            className={`nav-item ${activeSection === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveSection('profile')}
            title="My Profile"
          >
            <span className="nav-icon">👤</span>
            {sidebarOpen && <span>My Profile</span>}
          </button>

          <button
            className={`nav-item ${activeSection === 'courses' ? 'active' : ''}`}
            onClick={() => setActiveSection('courses')}
            title="My Courses"
          >
            <span className="nav-icon">📚</span>
            {sidebarOpen && <span>My Courses</span>}
          </button>

          <button
            className={`nav-item ${activeSection === 'students' ? 'active' : ''}`}
            onClick={() => setActiveSection('students')}
            title="Students"
          >
            <span className="nav-icon">👥</span>
            {sidebarOpen && <span>Students</span>}
          </button>

          <button
            className={`nav-item ${activeSection === 'communication' ? 'active' : ''}`}
            onClick={() => setActiveSection('communication')}
            title="Communication"
          >
            <span className="nav-icon">💬</span>
            {sidebarOpen && <span>Communication</span>}
          </button>

          <button
            className={`nav-item ${activeSection === 'certificates' ? 'active' : ''}`}
            onClick={() => setActiveSection('certificates')}
            title="Certificates"
          >
            <span className="nav-icon">🏆</span>
            {sidebarOpen && <span>Certificates</span>}
          </button>

          <hr className="nav-divider" />

          <button
            className="nav-item logout"
            onClick={() => {
              localStorage.clear();
              window.location.href = '/';
            }}
            title="Logout"
          >
            <span className="nav-icon">🚪</span>
            {sidebarOpen && <span>Logout</span>}
          </button>
        </nav>
      </aside>

      {/* Main Content */}
      <main className="faculty-content">
        {/* === PROFILE SECTION === */}
        {activeSection === 'profile' && facultyId && (
          <FacultyProfile facultyId={facultyId} />
        )}

        {/* === COURSES SECTION === */}
        {activeSection === 'courses' && (
          <div className="section-container">
            <div className="section-header">
              <h1>📚 My Courses</h1>
              <p>Manage your course content, publication status, and enrollments</p>
              <button 
                className="btn-primary btn-create-course"
                onClick={handleCreateCourse}
              >
                + Create New Course
              </button>
            </div>

            {courses.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📚</div>
                <h3>No courses yet</h3>
                <p>Create your first course to get started!</p>
                <button 
                  className="btn-primary"
                  onClick={handleCreateCourse}
                >
                  Create Course
                </button>
              </div>
            ) : (
              <div className="courses-grid">
                {courses.map(course => (
                  <div key={course.course_id} className="course-card-pro">
                    <div className="course-header-pro">
                      <h3>{course.course_title || course.course_name}</h3>
                      <span className={`badge badge-${(course.publish_status || course.status || '').toLowerCase()}`}>
                        {(course.publish_status || course.status || '').toLowerCase() === 'published' ? '✓ Published' : '✎ Draft'}
                      </span>
                    </div>

                    <p className="course-description">
                      {(course.description || 'No description provided').substring(0, 120)}...
                    </p>

                    <div className="course-meta-pro">
                      <div className="meta-item">
                        <span className="icon">📚</span>
                        <span>{course.duration_weeks || 12} weeks</span>
                      </div>
                      <div className="meta-item">
                        <span className="icon">👥</span>
                        <span>{course.enrollment_count ?? students.filter(s => s.course_id === course.course_id).length} students</span>
                      </div>
                      <div className="meta-item">
                        <span className="icon">📅</span>
                        <span>{new Date().toLocaleDateString()}</span>
                      </div>
                    </div>

                    <div className="course-actions-pro">
                      <button
                        className="btn-secondary btn-sm"
                        onClick={() => {
                          setSelectedCourse(course.course_id);
                          setActiveSection('students');
                        }}
                      >
                        View {course.enrollment_count ?? students.filter(s => s.course_id === course.course_id).length} Students
                      </button>

                      <button
                        className="btn-primary btn-sm"
                        onClick={() => handleEditCourse(course.course_id)}
                        title="Edit course details, content, and settings"
                      >
                        ✏️ Edit
                      </button>

                      {(course.publish_status || course.status || '').toLowerCase() === 'draft' ? (
                        <button
                          className="btn-success btn-sm"
                          onClick={() => handlePublish(course.course_id)}
                        >
                          Publish
                        </button>
                      ) : (
                        <button
                          className="btn-warning btn-sm"
                          onClick={() => handleUnpublish(course.course_id)}
                        >
                          Unpublish
                        </button>
                      )}

                      <button
                        className="btn-danger btn-sm"
                        onClick={() => handleDeleteCourse(course.course_id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* === STUDENTS SECTION === */}
        {activeSection === 'students' && (
          <div className="section-container">
            <div className="section-header">
              <h1>👥 Students</h1>
              <p>View and analyze student progress and performance</p>
            </div>

            {/* Filters */}
            <div className="filters-section">
              <div className="filter-group">
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="filter-input"
                />
              </div>

              <div className="filter-group">
                <select
                  value={courseFilter}
                  onChange={(e) => setCourseFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Courses</option>
                  {courses.map(course => (
                    <option key={course.course_id} value={course.course_id}>
                      {course.course_title || course.course_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {filteredStudents.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">👥</div>
                <h3>No students found</h3>
                <p>Students will appear here once they enroll in your courses</p>
              </div>
            ) : (
              <div className="students-section">
                {/* Summary Stats */}
                <div className="students-summary">
                  <div className="summary-card">
                    <span className="summary-label">Total Enrolled</span>
                    <span className="summary-value">{filteredStudents.length}</span>
                  </div>
                </div>

                {/* Students Table */}
                <div className="table-responsive">
                  <table className="students-table">
                    <thead>
                      <tr>
                        <th>Student Name</th>
                        <th>Course</th>
                        <th>Enrolled Date</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredStudents.map((student, idx) => (
                        <tr key={idx}>
                          <td>
                            <div className="student-cell">
                              <span className="student-avatar">{student.student_name?.charAt(0) || 'S'}</span>
                              <div>
                                <p className="student-name">{student.student_name || 'Unknown'}</p>
                                <p className="student-email">{student.email || 'No email'}</p>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="course-name">
                              {courses.find(c => c.course_id === student.course_id)?.course_title || 'Unknown'}
                            </span>
                          </td>
                          <td>{new Date(student.enrolled_at || student.enrollment_date || new Date()).toLocaleDateString()}</td>
                          <td>
                            <span className={`status-badge status-${student.enrollment_status || 'active'}`}>
                              {student.enrollment_status || 'Active'}
                            </span>
                          </td>
                          <td>
                            <button
                              className="btn-link"
                              onClick={() => {
                                setSelectedStudent(student);
                                setSelectedCourse(student.course_id);
                                setShowStudentDetail(true);
                              }}
                            >
                              View Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {activeSection === 'communication' && facultyId && (
          <div className="section-container">
            <FacultyCommunication facultyId={facultyId} facultyName={facultyName} />
          </div>
        )}

        {activeSection === 'certificates' && facultyId && (
          <div className="section-container">
            <FacultyCertificates facultyId={facultyId} />
          </div>
        )}
      </main>

      {/* Modals */}
      {showWizard && (
        <CourseWizard
          editMode={wizardMode === 'edit'}
          courseId={editingCourseId}
          programs={programs}
          facultyId={facultyId}
          onClose={() => {
            setShowWizard(false);
            setEditingCourseId(null);
            setWizardMode('create');
          }}
          onCourseCreated={() => {
            setShowWizard(false);
            setEditingCourseId(null);
            setWizardMode('create');
            loadAllData();
          }}
        />
      )}

      {showStudentDetail && selectedStudent && selectedCourse && (
        <div className="modal-overlay" onClick={() => setShowStudentDetail(false)}>
          <div className="modal-content-large" onClick={(e) => e.stopPropagation()}>
            <button
              className="modal-close"
              onClick={() => setShowStudentDetail(false)}
            >
              ✕
            </button>
            <StudentAnalytics
              courseId={selectedCourse}
              studentId={selectedStudent.student_id}
              studentName={selectedStudent.student_name}
              facultyId={facultyId}
            />
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default FacultyDashboard;
